import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import {
  MagnifyingGlass, SpeakerHigh, Stop, Translate, CaretDown, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ListenTranslateBar from "@/components/ListenTranslateBar";

// /faq — searchable Q&A library with category chips, TTS, and translation.

function speak(text, onEnd) {
  if (!("speechSynthesis" in window)) { toast.error("Text-to-speech not supported here."); return null; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.slice(0, 4000));
  u.rate = 1.0; u.pitch = 1.02; u.lang = "en-US";
  u.onend = onEnd; u.onerror = onEnd;
  window.speechSynthesis.speak(u);
  return u;
}
function stopSpeaking() { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }

export default function Faq() {
  const [data, setData] = useState({ categories: [], faqs: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [openIdx, setOpenIdx] = useState(-1);
  const [speakingIdx, setSpeakingIdx] = useState(-1);
  const [lang, setLang] = useState("en");
  const [langs, setLangs] = useState([]);
  const [translations, setTranslations] = useState({});  // { q_hash: { lang, text } }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (q.trim()) p.set("q", q.trim());
    http.get(`/ai/faq?${p.toString()}`)
      .then((r) => setData(r.data || { categories: [], faqs: [] }))
      .catch(() => setData({ categories: [], faqs: [] }))
      .finally(() => setLoading(false));
  }, [q, cat]);

  useEffect(() => {
    http.get("/translate/languages").then((r) => setLangs(r.data?.languages || [])).catch(() => {});
    return () => stopSpeaking();
  }, []);

  const grouped = useMemo(() => {
    const out = new Map();
    for (const f of data.faqs) {
      if (!out.has(f.category)) out.set(f.category, []);
      out.get(f.category).push(f);
    }
    return out;
  }, [data.faqs]);

  const catLabel = (key) => data.categories.find((c) => c.key === key)?.label || key;

  const toggle = (i) => setOpenIdx(openIdx === i ? -1 : i);

  const onSpeak = (i, text) => {
    if (speakingIdx === i) { stopSpeaking(); setSpeakingIdx(-1); return; }
    speak(text, () => setSpeakingIdx(-1));
    setSpeakingIdx(i);
  };

  const onTranslate = async (f) => {
    if (lang === "en") return;
    if (translations[f.q_hash]?.lang === lang) return;
    try {
      const { data: r } = await http.post("/translate", { text: f.answer, target_lang: lang, cache_key: `faq:${f.q_hash}` });
      setTranslations((t) => ({ ...t, [f.q_hash]: { lang, text: r.translated_text } }));
    } catch { toast.error("Translation failed."); }
  };

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] pt-32 md:pt-36 pb-24 min-h-screen">
      <div className="max-w-[900px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Frequently asked questions about NaturePulse." testidPrefix="ltbar-faq" />
      </div>
      <section className="max-w-3xl mx-auto px-5 md:px-8 mb-8">
        <div className="font-mono-tech text-[10px] md:text-xs tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / Ask NaturePulse · FAQ
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.9] tracking-tighter">
          The planet, in plain english.
        </h1>
        <p className="mt-4 max-w-2xl text-base md:text-lg text-[#3A3830] font-serif-display">
          Sixty everyday questions about air, water, wildlife, climate and how to live lighter — answered by a friendly educator. Listen aloud, translate to 23 languages, and take one small action today.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 mb-6">
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button
            onClick={() => setCat("")}
            aria-pressed={cat === ""}
            className={`font-mono-tech text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border ${cat === "" ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]" : "border-black/20 hover:border-[color:var(--np-brand,#B85F2A)]"}`}
          >
            All
          </button>
          {data.categories.map((c) => (
            <button
              key={c.key}
              data-testid={`faq-cat-${c.key}`}
              onClick={() => setCat(c.key)}
              aria-pressed={cat === c.key}
              className={`font-mono-tech text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border ${cat === c.key ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]" : "border-black/20 hover:border-[color:var(--np-brand,#B85F2A)]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} weight="thin" className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--np-muted,#3A3830)]" />
            <input
              data-testid="faq-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search questions…"
              className="w-full bg-transparent border border-black/20 focus:border-[color:var(--np-brand,#B85F2A)] outline-none pl-9 pr-3 py-2 font-mono-tech text-xs tracking-[0.1em]"
            />
          </div>
          {langs.length > 0 && (
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              aria-label="Translate answers"
              data-testid="faq-lang"
              className="bg-transparent font-mono-tech text-[10px] tracking-[0.24em] uppercase border border-black/20 px-2 py-2"
            >
              {langs.slice(0, 24).map((l) => (
                <option key={l.code} value={l.code}>{l.code.toUpperCase()}</option>
              ))}
            </select>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-5 md:px-8">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-black/5 animate-pulse" />
            ))}
          </div>
        ) : data.faqs.length === 0 ? (
          <div data-testid="faq-empty" className="rounded-xl border border-dashed border-black/20 p-8 text-center">
            <Sparkle size={26} weight="thin" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl mb-1">No questions yet.</div>
            <div className="text-sm text-[#3A3830]">
              An admin needs to seed the FAQ once. Meanwhile, click the <b>Ask NaturePulse</b> pill in the corner to ask anything.
            </div>
          </div>
        ) : (
          [...grouped.entries()].map(([catKey, items]) => (
            <div key={catKey} className="mb-8" data-testid={`faq-group-${catKey}`}>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
                / {catLabel(catKey)}
              </div>
              <ul className="space-y-2">
                {items.map((f, i) => {
                  const globalIdx = data.faqs.indexOf(f);
                  const open = openIdx === globalIdx;
                  const tr = translations[f.q_hash];
                  const answerText = tr && tr.lang === lang && lang !== "en" ? tr.text : f.answer;
                  return (
                    <li
                      key={f.q_hash}
                      className={`rounded-xl border ${open ? "border-[color:var(--np-brand,#B85F2A)] bg-white/70" : "border-black/10 bg-white/50"} backdrop-blur transition-colors`}
                    >
                      <button
                        onClick={() => toggle(globalIdx)}
                        className="w-full text-left px-4 py-3 flex items-center gap-3"
                        aria-expanded={open}
                        data-testid={`faq-item-${f.q_hash}`}
                      >
                        <span className="flex-1 font-serif-display text-[17px] md:text-lg leading-tight">{f.question}</span>
                        <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="px-4 pb-4 pt-1">
                          <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-[#1B4332]">
                            {answerText}
                          </div>
                          {f.action && (
                            <div className="mt-3 inline-flex items-start gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#1B3A2A] to-[#2E5A3A] text-[#F5EBDC] text-[12px] leading-tight">
                              <Sparkle size={11} weight="fill" className="mt-0.5 text-[#E4A15C] shrink-0" /> {f.action}
                            </div>
                          )}
                          <div className="mt-3 flex items-center gap-2 text-[color:var(--np-muted,#3A3830)]">
                            <button
                              onClick={() => onSpeak(globalIdx, answerText)}
                              data-testid={`faq-tts-${f.q_hash}`}
                              className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:text-[color:var(--np-text,#1B4332)]"
                            >
                              {speakingIdx === globalIdx ? <Stop size={11} weight="fill" /> : <SpeakerHigh size={11} weight="fill" />}
                              {speakingIdx === globalIdx ? "Stop" : "Listen"}
                            </button>
                            {lang !== "en" && (
                              <button
                                onClick={() => onTranslate(f)}
                                data-testid={`faq-translate-${f.q_hash}`}
                                className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:text-[color:var(--np-text,#1B4332)]"
                              >
                                <Translate size={11} weight="fill" /> {lang.toUpperCase()}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </section>
    </main>
  );
}
